<template>
  <div>
    <div
      v-if="task.id === 'create'"
      v-click-outside="() => createTask(true)"
      :class="[$.card, $.create_mode]"
    >
      <textarea
        ref="nameInput"
        v-model="name"
        :class="$.name_input"
        :placeholder="locale('board_task_enter_name')"
        rows="1"
        @click.stop
        @keydown.enter.prevent="() => createTask()"
      />
      <div
        ref="nameDisplay"
        :class="[$.name, $.create_mode]"
      >
        {{ name }}
      </div>
    </div>
    <div
      v-else
      ref="mainWrapper"
      v-tippy="{
        trigger: 'manual',
        theme: 'board-card',
        placement: 'right-start',
        content: tippyContent,
        boundary: tippyBoundary,
        popperOptions: {
          modifiers: {
            flip: {
              boundariesElement: tippyBoundary,
              flipVariationsByContent: true
            },
          },
        }
      }"
      :class="[
        $.card,
        highlightOverdue && isTaskOvedue ? $.overdue : '',
        settingsOpened ? $.settings_opened : '',
        'card-wrapper'
      ]"
      @click="cardClickHandler"
    >
      <div :class="$.header">
        <div
          :class="$.path"
          @mouseover.stop="toggleCardTip(true)"
          @mouseleave.stop="toggleCardTip(false)"
        >
          <span v-for="breadcrumb in breadcrumbs.shortened">
            {{ breadcrumb }}
          </span>
        </div>
        <div :class="[$.name_and_color]">
          <div
            :class="[
              $.color,
              'inner_clickable',
              paletteOpened ? $.palette_opened : '',
              nameEditing ? $.disable_hover : ''
            ]"
          >
            <vgp-color-palette
              ref="colorPalette"
              :class="$.color_picker"
              :disabled="isFieldDisabled('color', task)"
              select-type="single"
              select-field="square"
              :selected-values="[task.color]"
              :colors="colors.data"
              :select-width="72"
              :select-height="32"
              drop-position="fixed"
              small
              @showPalette="colorPaletteHandler"
              @selectColors="(e) => handleChangeData('color', e[0].id)"
            />
          </div>
          <textarea
            v-show="nameEditing"
            ref="nameInput"
            v-model="name"
            :class="[$.name_input, $.edit_mode]"
            :placeholder="locale('board_task_enter_name')"
            @click.stop
            @blur="updateName"
            @keydown.enter.prevent="updateName"
          />
          <div
            ref="nameDisplay"
            :class="[
              $.name,
              nameEditing ? $.edit_mode : ''
            ]"
            @mouseover.stop="toggleCardTip(true)"
            @mouseleave.stop="toggleCardTip(false)"
          >
            {{ name }}
          </div>
        </div>
      </div>

      <div :class="$.primary_info">
        <div
          :class="$.left"
          @click="hideResourcePopup"
        >
          <div
            v-if="groupingFieldInfo.key !== 'status'"
            :class="[
              $.status,
              'inner_clickable',
              isStatusEditing ? $.active : '',
              isFieldDisabled('status', task) ? $.disabled : ''
            ]"
            @click="(e) => handleClickStatusAndPriority(e, 'status')"
          >
            <svg-sprite
              :name="taskStatus.icon"
              type="bold"
              :size="16"
              :style="{ color: taskStatus.color }"
            />
            <div :class="$.status__label">
              {{ taskStatus.value }}
            </div>
          </div>
          <div
            v-if="groupingFieldInfo.key !== 'priority'"
            :class="[
              $.priority,
              'inner_clickable',
              isPriorityEditing ? $.active : '',
              isFieldDisabled('priority', task) ? $.disabled : ''
            ]"
            @click="(e) => handleClickStatusAndPriority(e, 'priority')"
          >
            <svg-sprite
              :name="taskPriority.icon"
              type="bold"
              :size="16"
              :style="{ color: taskPriority.color }"
            />
            <div :class="$.priority__label">
              {{ taskPriority.value }}
            </div>
          </div>
        </div>
        <div :class="$.right">
          <assigned-select
            :class="[
              $.resources,
              'inner_clickable',
              isFieldDisabled('resources', task) ? $.disabled : ''
            ]"
            :resources="formattedResources"
            :max-visible="3"
            :is-active="isAssignResourcesPopupShown"
            @onClick="showAssignResourcesPopup"
          />
        </div>
      </div>

      <div :class="$.footer">
        <div
          ref="dates"
          :class="$.dates"
          :style="{
            maxWidth: `${datesWidth}px`
          }"
          @click="hideResourcePopup"
        >
          <div :class="[$.date_picker_wrapper, 'inner_clickable']">
            <div
              v-show="!startDateShow"
              :class="[$.date_blocker, isFieldDisabled('start_date', task) ? $.disabled : '']"
              @click="(e) => showDatePicker(e, 'startDate')"
            ></div>
            <date-picker
              ref="startDatePicker"
              :class="$.date_picker"
              :value="task.start_date"
              :task-data="task"
              :customClass="'board_card_date_picker'"
              property="start_date"
              :calendar-icon="false"
              @showCalendar="(flag) => showCalendar('startDate', flag)"
              @change="(val) => handleChangeDate('start_date', val)"
            />
          </div>
          <div :class="$.separator">
            -
          </div>
          <div :class="[$.date_picker_wrapper, 'inner_clickable']">
            <div
              v-show="!endDateShow"
              :class="[$.date_blocker, isFieldDisabled('end_date', task) ? $.disabled : '']"
              @click="(e) => showDatePicker(e, 'endDate')"
            ></div>
            <date-picker
              ref="endDatePicker"
              :class="[$.date_picker, highlightOverdue && isTaskOvedue ? $.overdue : '']"
              :value="task.end_date"
              :task-data="task"
              :customClass="'board_card_date_picker'"
              property="end_date"
              :calendar-icon="false"
              @showCalendar="(flag) => showCalendar('endDate', flag)"
              @change="(val) => handleChangeDate('end_date', val)"
            />
          </div>
        </div>
        <div
          :class="$.status_bar"
          @click="hideResourcePopup"
        >
          <div
            v-if="task.hasComments"
            :class="[
              $.comments,
              'inner_clickable',
              task.newComments ? $.new : ''
            ]"
            @click="() => taskSettingsHandler('comments')"
          >
            <svg-sprite
              name="comments"
              :size="20"
            />
          </div>
          <div
            v-if="task.hasAttachments"
            :class="[$.attachments, 'inner_clickable']"
            @click="() => taskSettingsHandler('attachments')"
          >
            <svg-sprite
              name="attach"
              :size="20"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!(nameEditing || taskSettingsInfo)"
        :class="[$.context_menu, 'inner_clickable']"
        @click="hideResourcePopup"
      >
        <iconButton
          :icon="{ name: 'more-1', type: 'regular', size: 20 }"
          :colors="{ color: '#808080', bg: '#E5E5E5', hoverBg: '#CCCCCC', activeBg: '#B2B2B2', activeColor: '#808080' }"
          @onClick="(e) => toggleContextMenu({ ...task, columnId }, 'task', 'main', e.target)"
        />
      </div>

      <div
        v-if="taskSettingsInfo || task.id === 'draft'"
        :class="[
          $.mouse_events_blocker,
          task.id === 'draft' ? $.mouse_loader : ''
        ]"
        @click="taskSettingsHandler"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import AssignedSelect from './AssignedSelect.vue';
import iconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import DatePicker from '../../taskSettings/sections/dates/includes/DatePicker.vue';

import dateHelper from '$$helpers/dateFormats';
import { getWorkloadUnitLocale, getTaskBreadcrumbs } from '../../../helpers/boardHelpers.js';
import customHelper from '../../../helpers/custom';

const isDate = d => d instanceof Date && !isNaN(d);

export default {
  name: 'TaskCard',
  components: {
    AssignedSelect,
    iconButton,
    DatePicker,
  },
  inject: [
    'toggleContextMenu',
    'showTaskSettings',
    'isFieldDisabled',
    'checkPricingAccessOverdue',
    'getTasksMap',
    'getProjectName',
    'hideProjectSettingsPopup'
  ],
  props: {
    columnId: {
      type: Number,
      required: true,
      default: 0,
    },
    task: {
      type: Object,
      default: {}
    },
    projectResources: {
      type: Object,
      default: {}
    }
  },
  data() {
    return {
      locale: __,
      isAssignResourcesPopupShown: false,
      positionContextTaskMain: null,
      positionContextColumnValues: null,
      name: '',
      nameEditing: false,
      tippyTimeout: null,
      color: 1,
      paletteOpened: false,
      createTriggered: false,
      startDateShow: false,
      endDateShow: false
    };
  },
  created() {
    this.name = this.task.text || '';
    this.color = this.task.color || 1;
  },
  computed: {
    ganttId() {
      return +this.$route.params.projectId;
    },
    statusField() {
      return this.getField(false, 'status');
    },
    taskStatus() {
      if (this.task.id === 'create') return {};

      return this.statusField.options.find(opt => opt.id === this.task.status);
    },
    taskPriority() {
      if (this.task.id === 'create') return {};

      return this.priorityField.options.find(opt => opt.id === this.task.priority);
    },
    priorityField() {
      return this.getField(false, 'priority');
    },
    formattedResources() {
      const formattedResources = [];

      if (this.task.id !== 'create') {
        this.task.resources.forEach(({ resource_id, value }) => {
          const projectRes = this.projectResources[resource_id];

          if (!projectRes) return;

          const workloadType = projectRes.resourceProjects.find(p => p.projectId === this.task.gantt_id)?.type;

          formattedResources.push({
            resource_id,
            userId: projectRes.userId,
            name: projectRes.name,
            avatar: projectRes.photo || projectRes.resourcePhoto || `${GT.cdn}/imgs/default/default-ava.svg`,
            workload: value,
            workloadType,
            workloadUnit: workloadType ? __(getWorkloadUnitLocale({ workloadType })) : '',
          });
        });
      }

      return formattedResources;
    },
    breadcrumbs() {
      if (this.task.id === 'create') return {};

      const all = getTaskBreadcrumbs({
        parentId: this.task.parent,
        tasksMap: this.getTasksMap(),
      }).map(item => item.text);

      const delimeters = [];

      if (all.length === 2) {
        delimeters.push('/');
      } else if (all.length > 2) {
        delimeters.push('/', '...', '/');
      }

      const shortened = [...all];

      shortened.splice(1, all.length - 2, ...delimeters);

      return {
        all,
        shortened,
      };
    },
    isTaskOvedue() {
      if (this.task.id === 'create') return false;

      return new Date(this.task.end_date) < new Date() && this.task.progress < 1 && this.task.type === 'task';
    },
    tippyContent() {
      if (this.task.id === 'create') return '';

      return `
        <div class="task-name">${customHelper.formatTaskName(this.task.text)}</div>
        <div style="display: flex; align-items: center; width: 100%; padding: 0 4px; min-height: 24px;">
          <div style="background: #E5E5E5; min-height: 1px; flex-grow: 1;"></div>
        </div>
        <div>${this.locale('board_path')}:<br>${this.breadcrumbs.all.join(' / ') || this.getProjectName()}</div>
      `;
    },
    tippyBoundary() {
      return document.querySelector('.page-wrapper');
    },
    openedContextMenus() {
      if (this.task.id === 'create') return [];

      return this.contextMenus.filter(context =>
        context.type === 'task'
        && context.item.id === this.task.id
        && context.item.columnId === this.columnId
      );
    },
    isStatusEditing() {
      return this.openedContextMenus.some(context => context.menu === 'status');
    },
    isPriorityEditing() {
      return this.openedContextMenus.some(context => context.menu === 'priority');
    },
    highlightOverdue() {
      return this.checkPricingAccessOverdue('overdue_tasks') && this.$store.getters['headerView/isOverdueTasks'];
    },
    settingsOpened() {
      if (!this.taskSettingsInfo) return;

      const taskIdMatch = this.taskSettingsInfo.taskId === this.task.id;
      const columnMatch = this.taskSettingsInfo.columnId
        ? this.taskSettingsInfo.columnId === this.columnId
        : true;

      return taskIdMatch && columnMatch;
    },
    ...mapGetters({
      allFields: 'board/allFields',
      groupingFieldInfo: 'board/groupingFieldInfo',
      getField: 'board/getField',
      contextMenus: 'board/contextMenus',
      nameEditTaskKey: 'board/nameEditTaskKey',
      customFields: 'board/customFields',
      taskCardEditing: 'board/taskCardEditing',
      taskSettingsInfo: 'board/taskSettingsInfo',
      datesWidth: 'board/datesWidth',
      colors: 'teamSetting/colors',
      getResourceById: 'resourcesModel/getResourceById',
      getTask: 'tasksModel/getTask',
      getDataForBreadcrumbsByParentId: 'tasksModel/getDataForBreadcrumbsByParentId',
      getValuesMapForTask: 'columns/getValuesMapForTask',
      getAttachmentsByProjectAndTaskId: 'attachments/getAttachmentsByProjectAndTaskId',
    }),
  },
  watch: {
    'task.text': function (newVal) {
      this.name = newVal;
    },
    'task.color': function (newVal) {
      this.color = newVal;
    },
    name() {
      if (this.nameEditing) {
        setTimeout(() => {
          this.adjustInputHeight();
        });
      }
    },
    nameEditTaskKey(newVal) {
      if (newVal === `${this.columnId}:${this.task.id}`) {
        this.$refs.nameInput.style.height = `${this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28}px`;
        this.$refs.nameDisplay.style.width = `${this.$refs.nameDisplay.scrollWidth}px`;
        this.nameEditing = true;
        this.setTaskCardEditing({
          columnId: this.columnId,
          taskId: this.task.id
        });
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.nameInput.focus();
          }, 0);
        });
      }
    },
    isAssignResourcesPopupShown(newVal) {
      const taskCardEditing = newVal ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
      this.columnScrollListener(newVal);
    },
    isStatusEditing(newVal) {
      const taskCardEditing = newVal ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    isPriorityEditing(newVal) {
      const taskCardEditing = newVal ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    'task.id': {
      handler(newValue) {
        if (newValue === 'create') {
          this.nameEditing = true;
          setTimeout(() => {
            this.$refs.nameDisplay.style.width = `${this.$refs.nameInput.scrollWidth}px`;
            this.$refs.nameInput.style.height = `${this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28}px`;
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    updateName(e) {
      this.$refs.nameDisplay.style.width = '100%';
      this.nameEditing = false;
      this.$store.dispatch('board/clearNameEditTaskKey');
      this.handleChangeData('text', this.name.trim());
    },
    adjustInputHeight() {
      const newHeight = this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28;

      this.$refs.nameInput.style.height = `${newHeight}px`;
    },
    handleChangeData(key, value) {
      this.setTaskCardEditing(null);

      if (value === this.task[key]) return;

      switch (key) {
      case 'color':
        this.color = value;
        break;
      case 'text':
        this.name = value;
        break;
      }

      this.$emit('taskChange', { taskData: { ...this.task }, key, value });
    },
    onAssignResourcesPopupHide() {
      this.isAssignResourcesPopupShown = false;
    },
    showAssignResourcesPopup(event) {
      if (this.isFieldDisabled('resources', this.task) || this.isAssignResourcesPopupShown) return;

      gantt.callEvent('hideResourcesListForTask');

      const pos = event.currentTarget.getBoundingClientRectWrapper();

      pos.x += 8;

      gantt.callEvent('showResourcesListForTask', [pos, this.task.id, this.task.gantt_id, true, 'boardView', this.onAssignResourcesPopupHide]);
      this.isAssignResourcesPopupShown = true;
    },
    handleChangeDate(dateProp, newValue) {
      if (isDate(newValue) && isDate(this.task[dateProp])) {
        const oldValue = this.task[dateProp];
        const newValueWithLimitCheck = dateHelper.checkIfDateInLimit(newValue, true);

        const newDateClone = new Date(newValue.getTime());

        newDateClone.setHours(0, 0, 0, 0);

        const oldDateClone = new Date(oldValue.getTime());

        oldDateClone.setHours(0, 0, 0, 0);

        if (newDateClone.getTime() === oldDateClone.getTime()) return;

        if (newValueWithLimitCheck.getTime() !== newDateClone.getTime()) {
          newValue = newValueWithLimitCheck;
        }
      }

      const currentTaskDate = new Date(this.task[dateProp]);
      const [currentHours, currentMinutes] = [currentTaskDate.getHours(), currentTaskDate.getMinutes()];

      const newDate = this.getClosestDate(newValue, dateProp === 'start_date' ? 'future' : 'past');

      newDate.setHours(currentHours);
      newDate.setMinutes(currentMinutes);

      this.handleChangeData(dateProp, newDate);
    },
    getClosestDate(date, direction) {
      let resDate = date;
      const taskData = gantt.getTask(this.task.id);
      const projectCalendar = gantt.getCalendar(this.task.gantt_id);

      if (!gantt.isWorkDay(resDate, projectCalendar)) {
        resDate = gantt.getClosestWorkTime({
          date: resDate, dir: direction, unit: gantt.config.duration_unit, task: taskData,
        });
      }

      return resDate;
    },
    toggleCardTip(flag) {
      const tippy = this.$refs.mainWrapper._tippy;

      if (this.tippyTimeout) {
        clearTimeout(this.tippyTimeout);
      }

      if (flag) {
        this.tippyTimeout = setTimeout(() => {
          tippy.show();
        }, 300);
      } else {
        this.tippyTimeout = setTimeout(() => {
          tippy.hide();
        }, 100);
      }
    },
    cardClickHandler(e) {
      const isRemovedElementFromDatePickerClicked = !e.target.isConnected && e.composedPath().some(el => el.className.includes(this.$.date_picker_wrapper));

      if (isRemovedElementFromDatePickerClicked || e.target.closest('.inner_clickable')) return;

      this.taskSettingsHandler();
    },
    taskSettingsHandler(section = null) {
      if (this.task.id === 'draft') return;

      this.showTaskSettings(this.task.id, this.columnId, section);
    },
    columnScrollListener(activate) {
      const tasksContainer = this.$refs.mainWrapper.closest('.gantt_scroll');

      if (tasksContainer) {
        if (activate) {
          tasksContainer.addEventListener('scroll', this.hideResourcePopup);
        } else {
          tasksContainer.removeEventListener('scroll', this.hideResourcePopup);
        }
      }
    },
    hideResourcePopup() {
      gantt.callEvent('hideResourcesListForTask');
    },
    colorPaletteHandler(flag) {
      this.hideResourcePopup();
      const taskCardEditing = flag ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
      this.paletteOpened = flag;
    },
    handleClickStatusAndPriority(e, field) {
      return !this.isFieldDisabled(field, this.task)
        && this.toggleContextMenu({ ...this.task, columnId: this.columnId }, 'task', field, e.currentTarget);
    },
    createTask(isOutsideClick = false) {
      if (this.createTriggered) return;

      this.$emit('createTask', this.name.trim(), isOutsideClick);
      this.createTriggered = true;
    },
    showDatePicker(e, type) {
      this.hideProjectSettingsPopup();
      let fieldKey = type === 'startDate' ? 'start_date' : 'end_date';
      
      if (this.isFieldDisabled(fieldKey, this.task)) return;

      let picker = 'startDatePicker';

      if (type === 'startDate') {
        this.startDateShow = true;
      } else {
        this.endDateShow = true;
        picker = 'endDatePicker';
      }
      setTimeout(() => {
        this.$refs[picker].$el.querySelector('input').focus();
      }, 0);
    },
    showCalendar(type, flag) {
      if (!flag) {
        if (type === 'startDate') {
          this.startDateShow = false;
        } else {
          this.endDateShow = false;
        }
      }

      const taskCardEditing = this.startDateShow || this.endDateShow ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    ...mapActions({
      setTaskCardEditing: 'board/setTaskCardEditing',
    }),
  },
};
</script>

<style module="$" src="../less/card.less"></style>
